import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { CgMenuLeft } from "react-icons/cg";
import { FiSearch } from "react-icons/fi";
import Breadcrumbs from "../../components/Breadcrumbs";
import NewTableComponent from "../../components/NewTableComponent";
import VehicleFilterDropdown from "../../components/VehicleFilterDropdown";
import DateFilterDropdown from "../../components/DateFilterDropdown";
import { apiService } from "../../services/apiService";
import { fetchVehiclesDropdown } from "../../store/Slices/dropdownSlice";
import { expenseCategoryDropdown } from "../../store/Slices/TripSlice";
import { formatDate, hasTripExpensePermission, hasTripPermission, isSuperAdminRole } from "../../utils/helper";
import { TripPermission } from "../../utils/app-constant";
import { errorNotify } from "../../utils/toast";
import Notification from "../../components/Notification";
import ExportButton from "../../components/ExportButton";
import { useTripExpensesListingExport } from "./useTripExpensesListingExport";
import MobileListingFilters from "../../components/MobileListingFilters";
import "../../styles/Trip.css";
import "../../styles/Access.css";
import "../../styles/TripsList.css";
import "../../styles/Services.css";

const formatAmount = (amount) => {
  if (amount === null || amount === undefined || amount === "") return "-";
  const numericValue = Number(amount);
  if (!Number.isFinite(numericValue)) return String(amount);
  return numericValue.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const TripExpense = () => {
  const outletContext = useOutletContext() || {};
  const toggleMobileSidebar = outletContext?.toggleMobileSidebar;

  const dispatch = useDispatch();
  const { vehiclesDropdownData = [] } = useSelector(
    (state) => state.dropdowns || state.dropdown || {},
  );
  const { expenseCategoryDropdownData = [] } = useSelector((state) => state.trips);

  const [expenseList, setExpenseList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState({});

  const updateSearchFilters = React.useCallback((nextFilters) => {
    setCurrentPage(1);
    setSearchFilters(nextFilters);
  }, []);

  const handleSearchChange = React.useCallback((nextSearchText) => {
    setCurrentPage(1);
    setSearchText(nextSearchText);
  }, []);

  const canExportTripExpensePdf = useMemo(
    () =>
      isSuperAdminRole() ||
      hasTripExpensePermission("trip-expense-export-pdf") ||
      hasTripExpensePermission("export-pdf") ||
      hasTripPermission(TripPermission.TripExpenseExportPdf),
    [],
  );

  const canExportTripExpenseExcel = useMemo(
    () =>
      isSuperAdminRole() ||
      hasTripExpensePermission("trip-expense-export-excel") ||
      hasTripExpensePermission("export-excel") ||
      hasTripPermission(TripPermission.TripExpenseExportExcel),
    [],
  );

  const {
    hasActiveFilters,
    exportLoading,
    pdfExportLoading,
    excelExportLoading,
    handleExportPdf,
    handleExportExcel,
  } = useTripExpensesListingExport(searchText, searchFilters);

  useEffect(() => {
    dispatch(fetchVehiclesDropdown());
    dispatch(expenseCategoryDropdown());
  }, [dispatch]);

  const vehicleDropdownOptions = useMemo(() => {
    return (vehiclesDropdownData || []).map((v) => ({
      label: v.registration_number || v.label || String(v.id || v.value),
      value: v.id ?? v.value,
    }));
  }, [vehiclesDropdownData]);

  const categoryDropdownOptions = useMemo(() => {
    return (expenseCategoryDropdownData || []).map((item) => ({
      label: item.title || item.slug,
      value: item.slug,
    }));
  }, [expenseCategoryDropdownData]);

  const fetchTripExpenses = async () => {
    setLoading(true);
    try {
      const response = await apiService.getTripExpensesPaginated({
        page: currentPage,
        limit,
        search_text: searchText,
        searchFilters,
      });

      const list = response?.data?.results || response?.data?.data || [];
      const meta = response?.data?.meta || {};
      const total = meta?.totalItems ?? response?.data?.total ?? list.length;
      const pages = meta?.totalPages ?? Math.max(1, Math.ceil(total / limit));

      setExpenseList(list);
      setTotalItems(total);
      setTotalPages(pages);
    } catch (err) {
      errorNotify(err || "Failed to fetch trip expenses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTripExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit, searchText, searchFilters]);

  const columns = useMemo(
    () => [
      {
        name: "Trip ID",
        selector: (row) => row?.trip?.trip_id || row?.trip?.custom_id || row?.trip?.id || (row?.trip_id ? `TR-${row.trip_id}` : "-"),
        cell: (row) => (
          <span className="hyperlink">
            {row?.trip?.trip_id || row?.trip?.custom_id || row?.trip?.id || (row?.trip_id ? `TR-${row.trip_id}` : "-")}
          </span>
        ),
        minWidth: "140px",
        tableColumn: true,
        filterName: "tripIdFilter",
        type: "text",
      },
      {
        name: "Vehicle",
        selector: (row) => row?.vehicle?.registration_number || row?.vehicle_id || "-",
        cell: (row) => (
          <span>
            {row?.vehicle?.registration_number?.toUpperCase() ||
              (row?.vehicle_id ? `#${row.vehicle_id}` : "-")}
          </span>
        ),
        minWidth: "160px",
        tableColumn: true,
        filterName: "vehicleIdFilter",
        type: "select",
        isMulti: true,
        filterComponent: VehicleFilterDropdown,
        options: vehicleDropdownOptions,
      },
      {
        name: "Driver",
        selector: (row) => row?.driver?.username || "-",
        cell: (row) => row?.driver?.username || "-",
        minWidth: "160px",
      },
      {
        name: "Expense Category",
        selector: (row) => row?.expense_category?.title || "-",
        cell: (row) => row?.expense_category?.title || "-",
        minWidth: "180px",
        tableColumn: true,
        filterName: "tripExpenseCategoryFilter",
        type: "select",
        options: categoryDropdownOptions,
      },
      {
        name: "Amount (PKR)",
        selector: (row) => row?.amount,
        cell: (row) => <span>{formatAmount(row?.amount)}</span>,
        minWidth: "140px",
      },
      {
        name: "Created Date",
        selector: (row) => row?.created_at,
        cell: (row) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {row?.created_at ? formatDate(row.created_at) : "-"}
          </span>
        ),
        minWidth: "210px",
        tableColumn: true,
        filterName: "createdAtFilter",
        type: "select",
        filterComponent: DateFilterDropdown,
      },
    ],
    [vehicleDropdownOptions, categoryDropdownOptions],
  );

  const mobileFilters = useMemo(
    () => [
      { name: "Trip ID", filterName: "tripIdFilter", type: "text" },
      {
        name: "Vehicle",
        filterName: "vehicleIdFilter",
        type: "select",
        options: vehicleDropdownOptions,
      },
      {
        name: "Expense Category",
        filterName: "tripExpenseCategoryFilter",
        type: "select",
        options: categoryDropdownOptions,
      },
      {
        name: "Created Date",
        filterName: "createdAtFilter",
        filterComponent: DateFilterDropdown,
      },
    ],
    [categoryDropdownOptions, vehicleDropdownOptions],
  );

  return (
    <div className="trip_page trip_expense_page">

      {/* ── Mobile Header ── */}
      <div className="sub_page_mobile_header">
        <div className="sub_page_mobile_header_left">
          <button className="trips_mobile_menu_btn" onClick={toggleMobileSidebar}>
            <CgMenuLeft size={22} color="#1c1c1c" />
          </button>
          <h2 className="sub_page_mobile_title">Trip Expense</h2>
        </div>
      </div>

      {/* ── Mobile Export Row ── */}
      {(canExportTripExpensePdf || canExportTripExpenseExcel) && (
        <div className="sub_page_mobile_export_row">
          {canExportTripExpensePdf && (
            <ExportButton
              type="pdf"
              onClick={handleExportPdf}
              disabled={!hasActiveFilters || pdfExportLoading}
              loading={pdfExportLoading}
              hasActiveFilters={hasActiveFilters}
            />
          )}
          {canExportTripExpenseExcel && (
            <ExportButton
              type="excel"
              onClick={handleExportExcel}
              disabled={!hasActiveFilters || excelExportLoading}
              loading={excelExportLoading}
              hasActiveFilters={hasActiveFilters}
            />
          )}
        </div>
      )}

      <div className="desktop_only_el">
        <Breadcrumbs values={["Trip Management", "Trip Expense"]} />
      </div>

      <div className="access_view sub_page_desktop_header">
        <div className="d-flex align-items-center gap-3">
          <h2>Trip Expense</h2>
        </div>

        <div className="services_buttons">
          {canExportTripExpensePdf && (
            <ExportButton
              type="pdf"
              onClick={handleExportPdf}
              disabled={!hasActiveFilters || pdfExportLoading}
              loading={pdfExportLoading}
              hasActiveFilters={hasActiveFilters}
            />
          )}

          {canExportTripExpenseExcel && (
            <ExportButton
              type="excel"
              onClick={handleExportExcel}
              disabled={!hasActiveFilters || excelExportLoading}
              loading={excelExportLoading}
              hasActiveFilters={hasActiveFilters}
            />
          )}

          <Notification />
        </div>
      </div>

      {/* ── Desktop Table ── */}
      <div className="desktop_table_view">
      <NewTableComponent
        showAddBtn={false}
        columns={columns}
        data={expenseList}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={setLimit}
        totalItems={totalItems}
        totalPages={totalPages}
        onSearch={handleSearchChange}
        searchValue={searchText}
        loading={loading}
        paginationLoading={loading}
        setSearchFilters={updateSearchFilters}
        searchFilters={searchFilters}
        tableClassName="trip_expense_table"
      />
      </div>

      {/* ── Mobile Cards ── */}
      <div className="mobile_cards_view trips_list_container">
        <div className="mobile_search_bar_wrapper">
          <div className="mobile_search_input_box">
            <FiSearch className="search_icon_svg" />
            <input
              type="text"
              placeholder="Search trip expenses..."
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
        </div>

        <MobileListingFilters
          filters={mobileFilters}
          searchFilters={searchFilters}
          setSearchFilters={updateSearchFilters}
        />

        {loading ? (
          <div className="mobile_cards_loading">Loading…</div>
        ) : expenseList.length === 0 ? (
          <div className="mobile_no_data">No trip expenses found</div>
        ) : (
          expenseList.map((row, idx) => (
            <div key={row?.id ?? idx} className="mobile_data_card">
              {/* Header: Trip ID + Date */}
              <div className="mobile_card_header">
                <div className="mobile_card_id">
                  <span className="hyperlink">{row?.trip?.trip_id || row?.trip?.custom_id || row?.trip?.id || (row?.trip_id ? `TR-${row.trip_id}` : "—")}</span>
                </div>
                <span style={{ fontSize: "12px", color: "#64748b" }}>
                  {row?.created_at ? formatDate(row.created_at) : "—"}
                </span>
              </div>

              {/* Vehicle + Driver */}
              <div className="mobile_card_row">
                <div className="mobile_card_field">
                  <span className="mobile_card_label">Vehicle</span>
                  <span className="mobile_card_value">
                    {row?.vehicle?.registration_number?.toUpperCase() || (row?.vehicle_id ? `#${row.vehicle_id}` : "—")}
                  </span>
                </div>
                <div className="mobile_card_field">
                  <span className="mobile_card_label">Driver</span>
                  <span className="mobile_card_value">{row?.driver?.username || "—"}</span>
                </div>
              </div>

              {/* Expense Category + Amount */}
              <div className="mobile_card_row">
                <div className="mobile_card_field">
                  <span className="mobile_card_label">Category</span>
                  <span className="mobile_card_value">{row?.expense_category?.title || "—"}</span>
                </div>
                <div className="mobile_card_field">
                  <span className="mobile_card_label">Amount (PKR)</span>
                  <span className="mobile_card_amount">{formatAmount(row?.amount)}</span>
                </div>
              </div>
            </div>
          ))
        )}

        {/* Mobile Pagination */}
        {totalPages > 1 && (
          <div className="mobile_pagination">
            <button
              className="mobile_page_btn"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >← Prev</button>
            <span className="mobile_page_info">Page {currentPage} of {totalPages}</span>
            <button
              className="mobile_page_btn"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >Next →</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TripExpense;
